// The tyre module's own fetch helper.
//
// Deliberately not the operations provider's: that module exists to load the whole plant bootstrap,
// which a tyre user is refused by design. Importing it here would couple the tyre screens to an
// endpoint they must never need, and the first symptom would be a dashboard that will not load.
export const tpms = async (path, method = 'GET', data = null, signal = undefined) => {
  const response = await fetch(path, {
    method,
    signal,
    headers: { 'Content-Type': 'application/json' },
    ...(data ? { body: JSON.stringify(data) } : {}),
  });
  const payload = await response.json().catch(() => null);
  // An ended session, recovered rather than reported. This module does not use the operations shell
  // - a tyre account is refused the plant bootstrap that shell loads - so it carries that shell's
  // 401 handling itself, or every tyre screen simply shows "request failed" until somebody thinks
  // to reload. Where it is already on the login page the 401 is a refused password, not an expired
  // session, and redirecting would loop.
  if (response.status === 401 && !window.location.pathname.startsWith('/login')) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}`);
    throw new Error('Your session has ended. Please sign in again.');
  }
  if (!response.ok) throw new Error(payload?.message || `Request failed (${response.status}).`);
  // A successful action with nothing to return answers 204 and no body. That is not a failure.
  if (payload == null && (response.status === 204 || response.status === 205)) return {};
  if (payload == null) throw new Error('The service returned an invalid response.');
  return payload;
};

export const HEALTH_LABEL = {
  NORMAL: 'Normal',
  WARNING: 'Warning',
  CRITICAL: 'Critical',
  NO_DATA: 'No data',
  SENSOR_FAULT: 'Sensor fault',
};

// Severity order for sorting and for the headline colour, worst first.
export const HEALTH_RANK = {
  CRITICAL: 0, SENSOR_FAULT: 1, WARNING: 2, NO_DATA: 3, NORMAL: 4,
};

export const healthColour = (health) => ({
  NORMAL: 'success', WARNING: 'warning', CRITICAL: 'error', NO_DATA: 'default', SENSOR_FAULT: 'default',
}[health] || 'default');

// An alert type as a fitter would say it, rather than as the engine stores it.
export const ALERT_LABEL = {
  UNDER_PRESSURE: 'Under pressure',
  CRITICAL_UNDER_PRESSURE: 'Critically under pressure',
  OVER_PRESSURE: 'Over pressure',
  CRITICAL_OVER_PRESSURE: 'Critically over pressure',
  HIGH_TEMPERATURE: 'Running hot',
  CRITICAL_TEMPERATURE: 'Critically hot',
  RAPID_PRESSURE_LOSS: 'Losing pressure fast',
  SLOW_LEAK: 'Slow leak',
  SENSOR_OFFLINE: 'Sensor not reporting',
  LOW_SENSOR_BATTERY: 'Sensor battery low',
};

export const alertLabel = (type) => ALERT_LABEL[type] || String(type || '').replaceAll('_', ' ').toLowerCase();
